import { base64UrlToUint8Array } from '@/utils/base64url'
import { isIOSDevice, isRunningStandalone } from '@/utils/pwa'

import { pushApi } from '../api/pushApi'
import type { PushInscricaoRequest } from '../types/pushPayload'

/**
 * Integração com a Push API do navegador, sem React — usada pelo hook da tela,
 * pela ressincronização pós-login e pelo logout.
 */

export type PushSupport = 'supported' | 'unsupported' | 'ios-install-required'

/** Falha de push nunca pode travar o logout. */
const REVOKE_TIMEOUT_MS = 3_000

export function getPushSupport(): PushSupport {
  // iOS/iPadOS só expõe Push com o PWA instalado na tela inicial (16.4+), então
  // esse caso é checado antes da detecção de APIs.
  if (isIOSDevice() && !isRunningStandalone()) return 'ios-install-required'
  const supported =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  return supported ? 'supported' : 'unsupported'
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (getPushSupport() !== 'supported') return null
  // `getRegistration` (e não `ready`) para não ficar pendurado sem SW registrado (ex.: dev).
  const registration = await navigator.serviceWorker.getRegistration()
  return (await registration?.pushManager.getSubscription()) ?? null
}

function toInscricao(subscription: PushSubscription): PushInscricaoRequest {
  const { endpoint, keys } = subscription.toJSON()
  if (!endpoint || !keys?.p256dh || !keys.auth) {
    throw new Error('Inscrição de push sem endpoint ou chaves.')
  }
  return { endpoint, p256dh: keys.p256dh, auth: keys.auth }
}

/** Cria (ou reaproveita) a inscrição no navegador e registra na API. Exige permissão `granted`. */
export async function subscribeToPush(): Promise<PushSubscription> {
  // Sem SW registrado, `ready` nunca resolve — falha com explicação em vez de travar.
  if (!(await navigator.serviceWorker.getRegistration())) {
    throw new Error(
      'Service worker não registrado. Em desenvolvimento ele fica desligado — teste com `npm run preview:local`.',
    )
  }
  const registration = await navigator.serviceWorker.ready
  const applicationServerKey = base64UrlToUint8Array(await pushApi.chavePublica())
  const options: PushSubscriptionOptionsInit = { userVisibleOnly: true, applicationServerKey }

  let subscription: PushSubscription
  try {
    subscription = await registration.pushManager.subscribe(options)
  } catch (error) {
    // Inscrição existente com outra chave VAPID (chave rotacionada na API).
    if (!(error instanceof DOMException) || error.name !== 'InvalidStateError') throw error
    await (await registration.pushManager.getSubscription())?.unsubscribe()
    subscription = await registration.pushManager.subscribe(options)
  }

  await pushApi.inscrever(toInscricao(subscription))
  return subscription
}

/**
 * Desvincula na API (enquanto ainda há sessão) e cancela no navegador mesmo se
 * a API falhar — o endpoint cancelado passa a ser recusado pelo serviço de push.
 */
export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await getCurrentSubscription()
  if (!subscription) return
  try {
    await pushApi.cancelarInscricao(subscription.endpoint)
  } finally {
    await subscription.unsubscribe()
  }
}

/**
 * Reenvia a inscrição existente à API: liga o aparelho ao usuário logado agora e
 * propaga chaves renovadas. Nunca pede permissão nem cria inscrição nova.
 */
export async function syncPushSubscription(): Promise<void> {
  if (getPushSupport() !== 'supported' || Notification.permission !== 'granted') return
  const subscription = await getCurrentSubscription()
  if (subscription) await pushApi.inscrever(toInscricao(subscription))
}

/**
 * Logout: desvincula o aparelho para o próximo usuário não receber pushes do
 * anterior. Também cancela no navegador, para que ele não seja religado em
 * silêncio pela ressincronização. Nunca lança erro e não demora mais que alguns segundos.
 */
export async function revokePushSubscription(): Promise<void> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<void>((resolve) => {
    timeoutId = setTimeout(resolve, REVOKE_TIMEOUT_MS)
  })

  try {
    await Promise.race([unsubscribeFromPush(), timeout])
  } catch (error) {
    console.warn('[push] falha ao desvincular a inscrição no logout', error)
  } finally {
    clearTimeout(timeoutId)
  }
}
